import { capitalizeFirstCharacter } from "@/helpers/utils";
import { createSlice } from "@reduxjs/toolkit";
import { isEmpty } from "lodash";

const initialState = {
  productName: "ELDER_CARE",
  proposalNo: "", // PDE/IG/2833/7080000852
  proposalId: "",
  intent: "",
  activeState: 0,
  fromOrderTracking: false,
  inProgressStep: 0,
  proposalExpiry: false,
  isProposalCreationCompleted: false,
  policyPurchase: false,
  renewalViewProposal: false,
  validatedState: {
    // basicDetails: false,
    // lifestyleDetails: false,
    // medicalDetails: false,
    // kycDetails: false,
    // consentDetails: false,
    // paymentDetails: false,
    // bancaDetails: false,
  },
  portingDetails: {},
  basicDetails: {},
  lifestyleDetails: {},
  medicalDetails: {},
  bancaDetails: {},
  kycCompleted: false,
  proposalProgress: {
    proposalNo: "",
    proposalId: "",
    bucket: "",
    stage: 0,
  },
  proposal: {},
  refetchProposalFlag: false,
  additionalConsentInfo: {
    isAnyMemberAutoRejected: false,
    members: [],
  },
  ageBandChange: "",
  allMembersPortFromSamePolicy: true,
  isNstp: false,
  isAutoCol: false,
  isPEPCustomer: false,
  isPEPCustomerProposalRejected: false,
  pepMatchedStatus: "",
  endorsementRefetch: false,
  endorsementType: "",
  endorsementFields: {
    basicInfo: {
      proposerDetails: [],
      memberDetails: [],
      nomineeDetails: false,
      communicationDetails: [],
    },
    lifeStyleDetails: false,
    medicalDetails: false,
    portabilityDetails: false,
  },
  newlyAddedMembers: [],
  uploadedDocuments: {},
  isEndorsement: false,
  previousProposalDetails: {
    proposalNo: "",
    proposalId: "",
  },
  endorsementStatus: {
    currentEndorsmentNo: "",
    currentEndorsementId: "",
    isEndorsementDone: false,
    isConsentDone: false,
  },
  isFromEndorsementsActionsPage: false,
  isEndorsementFieldEditable: false,
  proposalInfo: "",
  // Enable multiple edits to the proposal form in one endorsement journey.
  immutableProposalData: {},
  immutableBasicDetails: {},
  immutableMedicalDetails: {},
  immutableLifestyleDetails: {},
  immutableAllMembersPortFromSamePolicy: true,
  optionalBenefits: {},
  premiumObj: {},
  endorsementBucket: "Initiated",
  isRenewalBadDataFlow: false,
  renewalEditableFields: [],
  renewalData: [],
  rebookPolicyDetails: [],
  showRebookOption: false,
  renewal: {
    isRenewalEditableFlow: false,
    feildEditable: false,
    renewalEditableFields: [],
  },
  deletedMemberPayload: [],
  tataEmpVerified: false,
  professionalEmailVerified: false,
  isProfessionalDiscountOptedInRenewal: false,
  customerInfoKyc: {},
  kycTraceId: "",
  isFromOrderSummary: false,
  renewalValidationFields: [],
  showRevisedPremium: true,
  isPhysicalCopyNeeded: false,
  otmStatus: {},
  policyId: "",
  cancelRebookMemberIdToggle: "No",
  isAmountBlockReq: "",
  isEmployeeDiscountEnabled: false,
  isNCDDiscountEnabled: false,
  planTypeRenewalEditFlow: "Floater",
  copsEditAllowed: false,
  ntId: "",
  isInstallmentCoverOpted: false,
  superuserDocuments: [],
};

export const proposalSlice = createSlice({
  name: "proposal",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    updateActiveState: (state, action) => {
      state.activeState = action.payload;
    },
    updateProposalExpiry: (state, action) => {
      state.proposalExpiry = action.payload;
    },
    updateInProgressStep: (state, action) => {
      state.inProgressStep = action.payload;
    },
    updateActiveStateByOne: (state) => {
      state.activeState += 1;
    },
    updateActiveStateByMinusOne: (state) => {
      state.activeState -= 1;
    },
    setValidatedState: (state, action) => {
      state.validatedState = action.payload;
    },
    updateValidatedState: (state, action) => {
      state.validatedState = {
        ...state.validatedState,
        ...action.payload,
      };
    },
    updateReadOnlyStatus: (state, action) => {
      state.readOnly = action.payload;
    },
    setPortingDetails: (state, action) => {
      state.portingDetails = action.payload;
    },
    setBasicDetails: (state, action) => {
      state.basicDetails = action.payload;
    },
    setImmutableBasicDetails: (state, action) => {
      state.immutableBasicDetails = action.payload;
    },
    setMedicalDetails: (state, action) => {
      state.medicalDetails = action.payload;
    },
    setImmutableMedicalDetails: (state, action) => {
      state.immutableMedicalDetails = action.payload;
    },
    setLifestyleDetails: (state, action) => {
      state.lifestyleDetails = action.payload;
    },
    setImmutableLifestyleDetails: (state, action) => {
      state.immutableLifestyleDetails = action.payload;
    },
    setBancaDetails: (state, action) => {
      state.bancaDetails = action.payload;
    },
    setGeneratedProposalNo: (state, action) => {
      state.proposalNo = action.payload;
    },
    setIntent: (state, action) => {
      state.intent = action.payload;
    },
    setProposalId: (state, action) => {
      state.proposalId = action.payload;
    },
    setPaymentInfo: (state, action) => {
      state.paymentInfo = action.payload;
    },
    setProposalProgress: (state, action) => {
      state.proposalProgress = action.payload;
    },
    setProposal: (state, action) => {
      state.proposal = action.payload;
    },
    setKycCompleted: (state, action) => {
      state.kycCompleted = action.payload;
    },
    setPolicyPurchase: (state, action) => {
      state.policyPurchase = action.payload;
    },
    setIsProposalCreationCompleted: (state, action) => {
      state.isProposalCreationCompleted = action.payload;
    },
    setRefetchProposal: (state, action) => {
      state.refetchProposalFlag = action.payload;
    },
    updateAdditionalConsentInfo: (state, action) => {
      state.additionalConsentInfo = action.payload;
    },
    setIsEndorsementDone: (state, action) => {
      state.endorsementStatus = {
        ...state.endorsementStatus,
        ...action.payload,
      };
    },
    clearProposalData: (state, action = { payload: {} }) => {
      // Use the payload to set specific state while clearing the proposal data.
      // Pass payload as empty in case you need to clear the whole state
      return { ...initialState, ...action.payload };
    },
    updateAgeBandChange: (state, action) => {
      state.ageBandChange = action.payload;
    },
    setFromOrderTracking: (state, action) => {
      state.fromOrderTracking = action.payload;
    },
    sethasAllMembersPortFromSamePolicy: (state, action) => {
      state.allMembersPortFromSamePolicy = action.payload;
    },
    sethasAllMembersPortFromSamePolicyImmutable: (state, action) => {
      state.immutableAllMembersPortFromSamePolicy = action.payload;
    },
    setIsNstp: (state, action) => {
      state.isNstp = action.payload;
    },
    setIsAutoCol: (state, action) => {
      state.isAutoCol = action.payload;
    },
    setIsPEPCustomer: (state, action) => {
      state.isPEPCustomer = action.payload;
    },
    setIsPEPCustomerProposalRejected: (state, action) => {
      state.isPEPCustomerProposalRejected = action.payload;
    },
    setPEPMatchedStatus: (state, action) => {
      state.pepMatchedStatus = action.payload;
    },
    setRenewalViewProposal: (state, action) => {
      state.renewalViewProposal = action.payload;
    },
    setTypeOfEndorsement: (state, action) => {
      state.endorsementType = action.payload;
    },
    updateEndorsementRefetch: (state, action) => {
      state.endorsementRefetch = action.payload;
    },
    setEndorsementFields: (state, action) => {
      state.endorsementFields = action.payload;
    },
    setNewlyAddedMembers: (state, action) => {
      state.newlyAddedMembers = action.payload;
    },

    setUploadedDocument: (state, action) => {
      state.uploadedDocuments = action.payload;
    },
    setIsEndorsement: (state, action) => {
      state.isEndorsement = action.payload;
    },
    setPreviousProposalDetails: (state, action) => {
      state.previousProposalDetails = {
        ...state.previousProposalDetails,
        ...action.payload,
      };
    },
    setIsFromEndorsementsActionsPage: (state, action) => {
      state.isFromEndorsementsActionsPage = action.payload;
    },
    setIsEndorsementFieldEditable: (state, action) => {
      state.isEndorsementFieldEditable = action.payload;
    },
    setProposalInfo: (state, action) => {
      state.proposalInfo = action.payload;
    },
    setImmutableProposalData: (state, action) => {
      state.immutableProposalData = action.payload;
    },
    setOptionalBenefits: (state, action) => {
      state.optionalBenefits = action.payload;
    },
    setPremiumObj: (state, action) => {
      state.premiumObj = action.payload;
    },
    setEndorsementBucket: (state, action) => {
      state.endorsementBucket = action.payload;
    },
    setIsRenewalBadData: (state, action) => {
      state.isRenewalBadDataFlow = action.payload;
    },
    setRenewalEditableFields: (state, action) => {
      state.renewalEditableFields = action.payload;
    },
    setRenewalData: (state, action) => {
      state.renewalData = action.payload;
    },
    setRebookPolicyDetails: (state, action) => {
      state.rebookPolicyDetails = action.payload;
    },
    setShowRebookOption: (state, action) => {
      state.showRebookOption = action.payload;
    },
    updateRenewalDetails: (state, action) => {
      state.renewal = { ...state.renewal, ...action.payload };
    },
    updateDeletedMemberPayload: (state, action) => {
      state.deletedMemberPayload = action.payload;
    },
    setTataEmpVerified: (state, action) => {
      state.tataEmpVerified = action.payload;
    },
    setCustomerInfoKyc: (state, action) => {
      state.customerInfoKyc = action.payload;
    },
    setKycTraceId: (state, action) => {
      state.kycTraceId = action.payload;
    },
    setIsFromOrderSummary: (state, action) => {
      state.isFromOrderSummary = action.payload;
    },
    setRenewalValidationFields: (state, action) => {
      state.renewalValidationFields = action.payload;
    },
    setShowRevisedPremium: (state, action) => {
      state.showRevisedPremium = action.payload;
    },
    setPhysicalCommunication: (state, action) => {
      state.isPhysicalCopyNeeded = action.payload;
    },
    setOtm: (state, action) => {
      state.otmStatus = action.payload;
    },
    setPolicyId: (state, action) => {
      state.policyId = action.payload;
    },
    setCancelRebookMemberIdToggle: (state, action) => {
      state.cancelRebookMemberIdToggle = action.payload;
    },
    setIsAmountBlockRequired: (state, action) => {
      // To Be Dispatch once's start receving from fetch api
      state.isAmountBlockReq = action.payload;
    },
    setIsEmployeeDiscountEnabled: (state, action) => {
      state.isEmployeeDiscountEnabled = action.payload;
    },
    setisNCDDiscountEnabled: (state, action) => {
      state.isNCDDiscountEnabled = action.payload;
    },
    setPlanTypeRenewalEditFlow: (state, action) => {
      state.planTypeRenewalEditFlow = action.payload;
    },
    setIsProfessionalEmailVerified: (state, action) => {
      state.professionalEmailVerified = action.payload;
    },
    setIsProfessionalDiscountOptedInRenewal: (state, action) => {
      state.isProfessionalDiscountOptedInRenewal = action.payload;
    },
    setCopsEditAllowed: (state, action) => {
      state.copsEditAllowed = action.payload;
    },
    setNtId: (state, action) => {
      state.ntId = action.payload;
    },
    setIsInstallmentCoverOpted: (state, action) => {
      state.isInstallmentCoverOpted = action.payload;
    },
    setSuperuserDocuments: (state, action) => {
      state.superuserDocuments = action.payload;
    },
  },
});

export const {
  updateActiveState,
  updateProposalExpiry,
  updateInProgressStep,
  updateActiveStateByOne,
  updateActiveStateByMinusOne,
  setValidatedState,
  updateValidatedState,
  updateReadOnlyStatus,
  setPortingDetails,
  setBasicDetails,
  setImmutableBasicDetails,
  setProposerData,
  setMedicalDetails,
  setImmutableMedicalDetails,
  setLifestyleDetails,
  setImmutableLifestyleDetails,
  setBancaDetails,
  setGeneratedProposalNo,
  setProposalId,
  clearQuoteData,
  setPaymentInfo,
  setProposalProgress,
  setProposal,
  setKycCompleted,
  setPolicyPurchase,
  setIsProposalCreationCompleted,
  setRefetchProposal,
  updateAdditionalConsentInfo,
  clearProposalData,
  updateAgeBandChange,
  setFromOrderTracking,
  sethasAllMembersPortFromSamePolicy,
  sethasAllMembersPortFromSamePolicyImmutable,
  setIsNstp,
  setIsAutoCol,
  setIsPEPCustomer,
  setIsPEPCustomerProposalRejected,
  setPEPMatchedStatus,
  setIntent,
  setRenewalViewProposal,
  updateEndorsementRefetch,
  setTypeOfEndorsement,
  setEndorsementFields,
  setNewlyAddedMembers,
  setUploadedDocument,
  setIsEndorsement,
  setPreviousProposalDetails,
  setIsEndorsementDone,
  setIsFromEndorsementsActionsPage,
  setIsEndorsementFieldEditable,
  setProposalInfo,
  setImmutableProposalData,
  setOptionalBenefits,
  setPremiumObj,
  setEndorsementBucket,
  setIsRenewalBadData,
  setRenewalEditableFields,
  setRenewalData,
  setRebookPolicyDetails,
  setShowRebookOption,
  updateRenewalDetails,
  updateDeletedMemberPayload,
  setTataEmpVerified,
  setCustomerInfoKyc,
  setKycTraceId,
  setIsFromOrderSummary,
  setRenewalValidationFields,
  setShowRevisedPremium,
  setPhysicalCommunication,
  setOtm,
  setPolicyId,
  setCancelRebookMemberIdToggle,
  setIsAmountBlockRequired,
  setIsEmployeeDiscountEnabled,
  setisNCDDiscountEnabled,
  setPlanTypeRenewalEditFlow,
  setIsProfessionalEmailVerified,
  setIsProfessionalDiscountOptedInRenewal,
  setCopsEditAllowed,
  setNtId,
  setIsInstallmentCoverOpted,
  setSuperuserDocuments,
} = proposalSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`

export const getActiveState = (state) => state.proposal?.activeState;
export const getProposalExpiry = (state) => state.proposal?.proposalExpiry;
export const getInProgressState = (state) => state.proposal?.inProgressStep;
export const getReadOnlyStatus = (state) => state.proposal?.readOnly;
export const getValidatedState = (state) => state.proposal?.validatedState;
export const getProposalData = (state) => state.proposal?.proposal;
export const getPortingDetails = (state) => state.proposal?.portingDetails;
export const getBasicDetails = (state) => state.proposal?.basicDetails;
export const getLifestyleDetails = (state) => state.proposal?.lifestyleDetails;
export const getMedicalDetails = (state) => state.proposal?.medicalDetails;
export const getBancaDetails = (state) => state.proposal?.bancaDetails;
export const getProposalNo = (state) => state.proposal?.proposalNo;
export const getIntent = (state) => state.proposal?.intent;
export const getProposalId = (state) => state.proposal.proposalId;
export const getPaymentInfo = (state) => state.proposal?.paymentInfo;
export const getProposalProgress = (state) => state.proposal?.proposalProgress;
export const getProposalBucket = (state) => {
  if (!isEmpty(state.proposal?.proposalProgress?.bucket)) {
    return state.proposal?.proposalProgress?.bucket;
  } else {
    return state.proposal?.proposal?.proposalStatus?.currentStatus || "";
  }
};
export const getProposal = (state) => state.proposal?.proposal;
export const getKycCompleted = (state) => state.proposal?.kycCompleted;
export const getProposalStatus = (state) =>
  state.proposal.proposal?.proposalStatus || {};
export const getProposalConsentData = (state) =>
  state.proposal.proposal?.consent || {};
export const getPolicyPurchase = (state) => state.proposal?.policyPurchase;
export const getIsProposalCreationCompleted = (state) =>
  state.proposal?.isProposalCreationCompleted;

export const getProposalFullName = (state) => {
  const { basicDetails, proposal } = state.proposal;
  if (!isEmpty(proposal)) {
    return [
      capitalizeFirstCharacter(proposal?.proposer?.firstName),
      capitalizeFirstCharacter(proposal?.proposer?.middleName),
      capitalizeFirstCharacter(proposal?.proposer?.lastName),
    ].join(" ");
  } else {
    //TODO: Set the propos
    if (!isEmpty(basicDetails.proposer)) {
      return capitalizeFirstCharacter(basicDetails.proposerfullName);
    } else {
      const proposerMember =
        !isEmpty(basicDetails) &&
        basicDetails.members.find((mem) => mem.relationship === "Self");
      if (!isEmpty(proposerMember)) {
        return capitalizeFirstCharacter(proposerMember.fullName);
      }
      return "";
    }
  }
};

export const getRefetchProposalFlag = (state) =>
  state.proposal.refetchProposalFlag;
export const getAddtionalConsentInfo = (state) =>
  state.proposal.additionalConsentInfo;

export const getAgeBandChange = (state) => state.proposal.ageBandChange;
export const getFromOrderTracking = (state) => state.proposal.fromOrderTracking;
export const gethasAllMembersPortFromSamePolicy = (state) =>
  state.proposal.allMembersPortFromSamePolicy;
export const gethasAllMembersPortFromSamePolicyImmutable = (state) =>
  state.proposal.immutableAllMembersPortFromSamePolicy;
export const getIsNstp = (state) => {
  if (state.proposal?.isNstp) {
    return true;
  } else {
    return false;
  }
};
export const getIsAutoCol = (state) => {
  let autoCol = state.proposal?.proposal?.memberDetails?.some(
    (member) => member?.memberStatus?.isAutoCOL
  );
  if (autoCol) {
    return true;
  } else {
    return false;
  }
};
export const getIsPEPCustomer = (state) => {
  return state.proposal.isPEPCustomer;
};
export const getIsPEPCustomerProposalRejected = (state) => {
  return state.proposal.isPEPCustomerProposalRejected;
};
export const getPEPMatchedStatus = (state) => {
  return state.proposal.pepMatchedStatus;
};
export const getRenewalViewProposal = (state) => {
  return state.proposal.renewalViewProposal;
};
export const getEndorsementRefetch = (state) => {
  return state.proposal.endorsementRefetch;
};
export const getTypeOfEndorsment = (state) => {
  return state.proposal.endorsementType;
};
export const getEndorsementFields = (state) => state.proposal.endorsementFields;
export const getNewlyAddedMembers = (state) => state.proposal.newlyAddedMembers;
export const getUploadedDocuments = (state) => state.proposal.uploadedDocuments;
export const getIsEndorsement = (state) => state.proposal.isEndorsement;
export const getPreviousProposalDetails = (state) =>
  state.proposal.previousProposalDetails;
export const getIsEndorsementDone = (state) => state.proposal.endorsementStatus;
export const getIsFromEndorsementsActionsPage = (state) =>
  state.proposal.isFromEndorsementsActionsPage;
export const getIsEndorsementFieldEditable = (state) =>
  state.proposal.isEndorsementFieldEditable;
export const getProposalInfo = (state) => state.proposal.proposalInfo;
export const getImmutableProposalData = (state) =>
  state.proposal.immutableProposalData;
export const getImmutableBasicDetails = (state) =>
  state.proposal.immutableBasicDetails;
export const getImmutableMedicalDetails = (state) =>
  state.proposal.immutableMedicalDetails;
export const getImmutableLifestyleDetails = (state) =>
  state.proposal.immutableLifestyleDetails;
export const getOptionalBenefitsProposal = (state) =>
  state.proposal.optionalBenefits;
export const getPremiumObj = (state) => state.proposal.premiumObj;
export const getEndorsementBucket = (state) => state.proposal.endorsementBucket;
export const getRenewalEditableFields = (state) =>
  state.proposal.renewalEditableFields;
export const getIsRenewalBadData = (state) =>
  state.proposal.isRenewalBadDataFlow;
export const getRenewalData = (state) => state.proposal.renewalData;
export const getRebookPolicyDetails = (state) =>
  state.proposal.rebookPolicyDetails;
export const getShowRebookOption = (state) => state.proposal.showRebookOption;
export const getRenewalDetail = (state) => state.proposal.renewal;
export const getDeletedMemberPayload = (state) =>
  state.proposal.deletedMemberPayload;
export const getTataEmpVerified = (state) => state.proposal.tataEmpVerified;
export const getCustomerInfoKyc = (state) => state.proposal.customerInfoKyc;
export const getKycTraceId = (state) => state.proposal.kycTraceId;
export const getIsFromOrderSummary = (state) =>
  state.proposal.isFromOrderSummary;
export const getRenewalValidationsFields = (state) =>
  state.proposal.renewalValidationFields;
export const getShowRevisedPremium = (state) =>
  state.proposal.showRevisedPremium;
export const getPhysicalCommunication = (state) =>
  state.proposal.isPhysicalCopyNeeded;
export const getOtm = (state) => state.proposal.otmStatus;
export const getPolicyId = (state) => state.proposal.policyId;
export const getCancelRebookMemberIdToggle = (state) =>
  state.proposal.cancelRebookMemberIdToggle;
export const getIsAmountBlockRequired = (state) =>
  state.proposal.isAmountBlockReq;
export const getIsEmployeeDiscountEnabled = (state) =>
  state.proposal.isEmployeeDiscountEnabled;
export const getIsNCDDiscountEnabled = (state) =>
  state.proposal.isNCDDiscountEnabled;
export const getPlanTypeRenewalEditFlow = (state) =>
  state.proposal.planTypeRenewalEditFlow;
export const getProfessionalEmailVerified = (state) =>
  state?.proposal?.professionalEmailVerified;
export const getIsProfessionalDiscountOptedInRenewal = (state) =>
  state?.proposal?.isProfessionalDiscountOptedInRenewal;
export const getCopsEditAllowed = (state) => state.proposal?.copsEditAllowed;
export const getNtId = (state) => state.proposal.ntId;
export const getIsInstallmentCoverOpted = (state) =>
  state.proposal?.isInstallmentCoverOpted;
export const getSuperuserDocuments = (state) =>
  state.proposal?.superuserDocuments;

export default proposalSlice.reducer;
